import * as actions from 'api/core/actions';
import type { ReportProjectIds } from 'redux/modules/superreports.types';
import { FORECAST_PAGE_LIMIT } from 'routes/forecast/redux/modules/forecast.types';
import type { TaskAnalyticsSaveContext } from 'types/analytics';
import { objectToQueryString } from 'utils/url-params';
import type { Forecast } from '../types/forecast';

export interface ForecastFilter {
  /** Which user ids to query? */
  user_ids?: Array<number | null>;
  /** Which project ids to query? */
  project_ids?: ReportProjectIds;
  forecast_ids?: Array<number>;
  since: string | null;
  until: string | null;
  completed: boolean | null;
  page?: number;
  limit?: number;
  sort?: 'from' | 'to' | 'id' | 'user_name' | 'updated_at';
  order?: 'asc' | 'desc';
}

export interface ForecastRequest {
  id: number | null;
  project_id: number | null | undefined;
  label_ids: Array<number>;
  title: string;
  description: string;
  from: string;
  to: string;
  completed_at?: string | null;
  estimated_minutes: number;
  users: Array<{ id: number }>;
  context: TaskAnalyticsSaveContext;
}

export const forecastApi = {
  getForecasts({
    user_ids,
    project_ids,
    forecast_ids,
    since,
    until,
    limit,
    page,
    order,
    sort,
    completed,
  }: Partial<ForecastFilter>): Promise<Array<Forecast>> {
    // When user_ids is empty or very large, omit it entirely - the backend will return all forecasts
    // This avoids URL length limits (8KB) when "All users" is selected
    const shouldOmitUserIds = !user_ids || user_ids.length === 0;
    const params = {
      page: page ?? 1,
      sort: sort ?? 'from',
      order: order ?? 'desc',
      limit: FORECAST_PAGE_LIMIT,
      since,
      upto: until,
      user_ids: shouldOmitUserIds
        ? null
        : user_ids.map(id => (id == null ? 'unassigned' : id)).join(','),
      project_ids: Array.isArray(project_ids) ? project_ids.join(',') : project_ids,
      forecast_ids: forecast_ids ? forecast_ids.join(',') : null,
      per_page: limit ? limit : null,
      completed,
    };
    const queryString = objectToQueryString(params);

    return actions.Get(`forecasts?${queryString}`);
  },

  async getForecast(id: number): Promise<Forecast> {
    const params = objectToQueryString({ forecast_ids: [id] });
    const list = await actions.Get(`forecasts?${params}`);
    return list[0];
  },

  saveForecast(forecast: ForecastRequest): Promise<Forecast> {
    if (typeof forecast.id === 'number') {
      return actions.Patch(`forecasts/${forecast.id}`, { forecast });
    }

    return actions.Post('forecasts', {
      forecast,
    });
  },

  deleteForecast(id: number): Promise<void> {
    return actions.Delete(`forecasts/${id}`);
  },

  // very naive implementation faking bulk updates, to be replaced once backend supports
  bulkUpdateForecasts: async (payload: Array<Forecast>) => {
    const requests = payload.map(forecast => {
      return actions.Patch(`forecasts/${forecast.id}`, { forecast });
    });
    const results = await Promise.all(requests);

    return {
      created_ids: [] as Array<number>,
      updated_ids: results.map(r => r.id),
      deleted_ids: [] as Array<number>,
    };
  },
  bulkDeleteForecasts: async (payload: Array<number>) => {
    const requests = payload.map(id => {
      return actions.Delete(`forecasts/${id}`);
    });

    const results = await Promise.all(requests);

    return {
      created_ids: [] as Array<number>,
      updated_ids: [] as Array<number>,
      deleted_ids: results.map(r => r),
    };
  },
};
